import { useState } from 'react'
import {
  useGantiAkun, useGrup, useKoneksi, useKredensial, useMuatKredensial, usePeta,
  useTambahGrup, useTukarTokenMeta, useUbahAkun, useUbahGrup, useUjiKoneksi,
} from '../../../domains/tenancy/api'
import TambahAkun from './TambahAkun'
import { tanggal } from '../../../shared/lib/waktu'
import {
  Baris, Bendera, Body, Chip, Gagal, Isian, Kolom2, Kosong, Memuat, Panel, Tabel, Tombol,
} from '../../../shared/ui'

const NADA_KAP = { bisa: 'ok', tidak_bisa: 'idle', belum_dibangun: 'warn' }

/**
 * Peta kapabilitas platform + grup Telegram.
 *
 * Mengikuti `desain/smova-admin.html`: SELURUH platform ditampilkan, bukan cuma yang
 * sudah tersambung. Itu yang menjawab "bagaimana menambah Facebook Page" dengan jujur —
 * barisnya ada, akunnya kosong, dan kapabilitasnya bertanda *belum dibangun*.
 *
 * Daftar yang cuma memuat Instagram membuat operator menyimpulkan fiturnya HILANG,
 * padahal ia memang belum dibangun. Dua keadaan yang berbeda, dan yang kedua punya
 * jadwal.
 */
function PetaPlatform({ id, t }) {
  const q = usePeta(id)
  const uji = useUjiKoneksi(id)
  const koneksi = useKoneksi(id)
  const ubahAkun = useUbahAkun(id)
  const gantiAkun = useGantiAkun(id)
  const [hasil, setHasil] = useState({})
  // Platform yang sedang diganti akunnya, dan isian formulirnya. Satu pada satu waktu:
  // dua formulir terbuka bersamaan di layar sempit berarti dua tombol Simpan yang mirip.
  const [tukar, setTukar] = useState(null)
  const [fTukar, setFTukar] = useState({ id: '', token: '' })

  if (q.isLoading) return <Memuat tinggi={4} />
  if (q.isError) return <Gagal galat={q.error} ulangi={q.refetch} />

  // Satu daftar kunci untuk kepala DAN sel, supaya keduanya tidak bisa menyimpang.
  const kunciKap = [...new Map((q.data ?? [])
    .flatMap((p) => p.kapabilitas ?? [])
    .map((k) => [k.kunci, k.label])).keys()]

  return (
    <Panel judul={t('sb.platformJudul')} catatan={t('sb.platformCatatan')}>
      {/* GALAT TOMBOL AKTIFKAN/NONAKTIFKAN DITAMPILKAN. Sampai 28 September tombol itu
          mengirim `id` padahal `useUbahAkun` membaca `akunId`, jadi setiap tekanan
          ditolak 422 — dan layar diam, sehingga cacatnya bertahan 18 hari. */}
      {ubahAkun.isError && (
        <Bendera nada="bad" role="alert">{ubahAkun.error?.message}</Bendera>
      )}
      <Tabel
        kepala={[
          t('sb.kolPlatform'), t('sb.kolAkun'),
          // KEPALA DARI GABUNGAN SEMUA BARIS, bukan dari baris pertama.
          //
          // Bentuk lamanya membaca `q.data[0].kapabilitas`. Selama tiap platform punya
          // kapabilitas yang sama persis itu benar — dan diam-diam salah pada hari sebuah
          // platform punya jumlah berbeda: jumlah kepala tidak lagi sama dengan jumlah
          // sel, dan kolomnya bergeser satu tanpa satu pun galat. Yang membacanya melihat
          // "Terbitkan" di bawah kepala "Balas komentar".
          ...kunciKap.map((kunci) => (q.data ?? [])
            .flatMap((p) => p.kapabilitas ?? [])
            .find((k) => k.kunci === kunci)?.label ?? kunci),
          '',
        ]}
      >
        {(q.data ?? []).map((p) => (
          <Baris key={p.platform}>
            <td className={p.adapter_ada ? 'font-semibold' : 'font-semibold text-ink3'}>
              {p.label}
            </td>
            <td className="font-mono text-[11.5px] text-ink2">
              {p.akun
                ? <span className={p.akun_aktif ? undefined : 'line-through'}>{p.akun}</span>
                : <span className="text-ink3">{t('sb.belumDihubungkan')}</span>}
              {/* DUA AKUN AKTIF TIDAK PUNYA JAWABAN BENAR, dan layar harus
                  mengatakannya. Sebelum ini keadaan itu diringkas jadi satu nama, dan
                  yang ditampilkan bukan yang dipakai sistem. */}
              {p.akun_aktif_jumlah > 1 && (
                <span className="block mt-1">
                  <Chip nada="bad">
                    {t('sb.akunGanda', { n: p.akun_aktif_jumlah })}
                  </Chip>
                </span>
              )}
            </td>
            {/* Dipetakan dari DAFTAR KUNCI yang sama dengan kepalanya, bukan dari
                kapabilitas baris ini. Baris yang tidak punya sebuah kapabilitas tetap
                mengisi selnya dengan "—" — sel yang hilang menggeser seluruh kolom di
                kanannya, dan pergeseran itu tidak menimbulkan galat apa pun. */}
            {kunciKap.map((kunci) => {
              const k = (p.kapabilitas ?? []).find((x) => x.kunci === kunci)
              return (
                <td key={kunci}>
                  {k ? (
                    <Chip nada={NADA_KAP[k.keadaan] ?? 'idle'} titik={false}>
                      {t(`sb.kap.${k.keadaan}`)}
                    </Chip>
                  ) : <span className="text-ink3">—</span>}
                </td>
              )
            })}
            {/* UJI KONEKSI — menyimpan token dan token yang BEKERJA dua hal berbeda.
                Paling sering salah di Facebook, dan salahnya diam: Page token dan User
                token sama-sama string panjang dari aplikasi Meta yang sama, tapi cuma
                yang pertama boleh menerbitkan ke halaman. */}
            <td className="whitespace-nowrap text-right">
              {p.akun && (
                <Tombol
                  kecil
                  jenis="sekunder"
                  disabled={ubahAkun.isPending}
                  onClick={() => {
                    // DICOCOKKAN LEWAT ID AKUNNYA, bukan lewat platformnya.
                    //
                    // `find` atas platform mengembalikan yang PERTAMA ketemu — dan saat
                    // sebuah tenant sempat punya dua akun Instagram aktif, yang pertama
                    // bukan yang sedang ditampilkan baris ini. Tombolnya lalu
                    // menonaktifkan akun LAIN, layar tidak berubah, dan operator
                    // melaporkannya sebagai "tombolnya tidak berfungsi". Terjadi
                    // sungguhan 14 September.
                    const a = (koneksi.data?.akun ?? [])
                      .find((x) => x.external_account_id === p.akun)
                    if (a) ubahAkun.mutate({ akunId: a.id, aktif: !p.akun_aktif },
                                           { onSuccess: () => q.refetch() })
                  }}
                >
                  {p.akun_aktif ? t('adm.nonaktifkan') : t('adm.aktifkan')}
                </Tombol>
              )}{' '}
              {/* GANTI AKUN — satu perbuatan, bukan empat langkah di dua layar.
                  Sampai 14 September mengganti akun Instagram menuntut: nonaktifkan di
                  sini, isi ID di layar Tambah akun, gulir balik ke panel Kredensial
                  untuk tokennya, lalu uji. Langkah keduanya memasang jebakan — "token
                  sudah ada" benar secara harfiah dan menyesatkan secara praktis, karena
                  token yang ada milik akun LAMA. */}
              {p.adapter_ada && (
                <Tombol
                  kecil
                  jenis="sekunder"
                  onClick={() => {
                    setTukar(tukar === p.platform ? null : p.platform)
                    setFTukar({ id: '', token: '' })
                    gantiAkun.reset()
                  }}
                >
                  {tukar === p.platform ? t('umum.batal') : t('sb.gantiAkun')}
                </Tombol>
              )}{' '}
              {p.akun_aktif && p.adapter_ada && (
                <>
                  <Tombol
                    kecil
                    jenis="sekunder"
                    disabled={uji.isPending}
                    onClick={() => uji.mutate(p.platform, {
                      onSuccess: (d) => setHasil((x) => ({ ...x, [p.platform]: d })),
                      onError: (e) => setHasil((x) => ({
                        ...x, [p.platform]: { ok: false, pesan: e.message },
                      })),
                    })}
                  >
                    {t('sb.uji')}
                  </Tombol>{' '}
                  {hasil[p.platform] && (
                    <Chip nada={hasil[p.platform].ok ? 'ok' : 'bad'} titik={false}>
                      {hasil[p.platform].pesan}
                    </Chip>
                  )}
                </>
              )}
            </td>
          </Baris>
        ))}
      </Tabel>

      {/* SATU FORMULIR UNTUK SATU PERBUATAN. ID akun baru DAN tokennya bersama-sama:
          keduanya berubah bersamaan di dunia nyata, dan memisahkannya jadi dua layar
          adalah yang membuat operator memasang akun baru dengan token lama. */}
      {tukar && (
        <div className="p-3.5 border-t border-line flex flex-col gap-3 max-w-[70ch]">
          <Bendera nada="warn" judul={t('sb.gantiAkunJudul', { platform: tukar })}>
            <p>{t('sb.gantiAkunPeringatan')}</p>
          </Bendera>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
              {t('sb.gantiAkunId')}
            </span>
            <Isian value={fTukar.id} placeholder="17841467372683356"
                   onChange={(e) => setFTukar({ ...fTukar, id: e.target.value })} />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
              {t('sb.gantiAkunToken')}
            </span>
            <Isian type="password" value={fTukar.token} autoComplete="off"
                   onChange={(e) => setFTukar({ ...fTukar, token: e.target.value })} />
            <span className="text-[11.5px] text-ink3">{t('sb.gantiAkunTokenKet')}</span>
          </label>

          <span className="flex flex-wrap items-center gap-2">
            <Tombol
              disabled={gantiAkun.isPending || !fTukar.id.trim()}
              onClick={() => gantiAkun.mutate(
                { platform: tukar, external_account_id: fTukar.id.trim(),
                  token: fTukar.token.trim() || null },
                { onSuccess: () => { setFTukar({ id: '', token: '' }); q.refetch() } })}
            >
              {gantiAkun.isPending ? t('umum.menyimpan') : t('sb.gantiAkunSimpan')}
            </Tombol>
            <Tombol jenis="sekunder" onClick={() => setTukar(null)}>{t('umum.batal')}</Tombol>
          </span>

          {gantiAkun.isError && (
            <Bendera nada="bad" role="alert">{gantiAkun.error?.message}</Bendera>
          )}
          {/* BERHASIL PUN BISA SETENGAH. Akun berganti tanpa token berganti adalah
              keadaan yang menghasilkan "Object with ID … does not exist" saat menguji,
              dan diam di sini berarti operator baru tahu saat menekan Uji. */}
          {gantiAkun.isSuccess && (
            <Bendera nada={gantiAkun.data?.peringatan ? 'warn' : 'ok'}>
              <p>
                {t('sb.gantiAkunSelesai', {
                  lama: gantiAkun.data?.akun_lama || '—',
                  baru: gantiAkun.data?.akun_baru || '—',
                })}
              </p>
              {gantiAkun.data?.peringatan && <p>{gantiAkun.data.peringatan}</p>}
            </Bendera>
          )}
        </div>
      )}

      <p className="m-0 px-3.5 py-2.5 text-[11.5px] text-ink2 border-t border-line max-w-[80ch]">
        {t('sb.platformKaki')}
      </p>
    </Panel>
  )
}

/**
 * GRUP TELEGRAM — tempat notifikasi tim dikirim.
 *
 * Tidak ada tombol Hapus. Grup yang dibuang membawa serta jejak ke mana notifikasi
 * pernah dikirim, dan `chat_id`-nya langsung bisa diklaim tenant lain tanpa ada yang
 * tahu ia pernah milik siapa.
 */
function GrupTelegram({ id, t }) {
  const q = useGrup(id)
  const tambah = useTambahGrup(id)
  const ubah = useUbahGrup(id)
  const [buka, setBuka] = useState(false)
  const [f, setF] = useState({ chat_id: '', label: '' })

  return (
    <Panel
      judul={t('sb.grupJudul')}
      catatan={t('sb.grupCatatan')}
      aksi={
        <Tombol onClick={() => { setBuka(!buka); tambah.reset() }}>
          {buka ? t('umum.batal') : t('sb.tambahGrup')}
        </Tombol>
      }
    >
      {buka && (
        <div className="p-3.5 border-b border-line flex flex-col gap-3 max-w-[64ch]">
          <div className="grid grid-cols-2 gap-2.5">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                {t('sb.kolChatId')}
              </span>
              <Isian
                value={f.chat_id}
                placeholder="-1001234567890"
                onChange={(e) => setF({ ...f, chat_id: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                {t('sb.kolLabel')}
              </span>
              <Isian
                value={f.label}
                placeholder="Tim Marketing"
                onChange={(e) => setF({ ...f, label: e.target.value })}
              />
            </label>
          </div>
          <p className="m-0 text-[11.5px] text-ink3">{t('sb.chatIdCatatan')}</p>
          {tambah.isError && (
            <Bendera nada="bad" role="alert">{tambah.error?.message}</Bendera>
          )}
          <div>
            <Tombol
              disabled={!f.chat_id.trim() || tambah.isPending}
              onClick={() => tambah.mutate(
                { chat_id: Number(f.chat_id), label: f.label.trim() || null },
                { onSuccess: () => { setBuka(false); setF({ chat_id: '', label: '' }) } },
              )}
            >
              {tambah.isPending ? t('umum.menyimpan') : t('sb.simpanGrup')}
            </Tombol>
          </div>
        </div>
      )}

      {q.isLoading && <Memuat tinggi={3} />}
      {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

      {q.data && (q.data.length === 0 ? (
        <Kosong judul={t('sb.grupKosong')} keterangan={t('sb.grupKosongKet')} />
      ) : (
        <Tabel kepala={[t('sb.kolLabel'), t('sb.kolChatId'), t('sb.kolKeadaan'), '']}>
          {q.data.map((g) => (
            <Baris key={g.id}>
              <td className={g.is_active ? 'font-semibold' : 'font-semibold text-ink3'}>
                {g.label || '—'}
              </td>
              <td className="font-mono text-[11.5px] text-ink2">{g.chat_id}</td>
              <td>
                <Chip nada={g.is_active ? 'ok' : 'idle'}>
                  {g.is_active ? t('adm.aktif') : t('adm.nonaktif')}
                </Chip>
              </td>
              <td className="text-right">
                <Tombol
                  kecil
                  jenis="sekunder"
                  disabled={ubah.isPending}
                  onClick={() => ubah.mutate({ id: g.id, aktif: !g.is_active })}
                >
                  {g.is_active ? t('adm.nonaktifkan') : t('adm.aktifkan')}
                </Tombol>
              </td>
            </Baris>
          ))}
        </Tabel>
      ))}
    </Panel>
  )
}

/**
 * KREDENSIAL — apa yang ada, kapan kedaluwarsa. TIDAK PERNAH nilainya.
 *
 * Nilainya bytea hasil pgcrypto dan Python memang tidak bisa membacanya (ADR-049).
 * Panel ini mengikuti batas itu, bukan melawannya. Yang 'rotated' ikut tampil supaya
 * rotasi punya jejak yang terlihat — token yang diganti tidak hilang, ia berhenti
 * dipakai, dan bedanya penting saat menelusuri sejak kapan sambungan berhenti bekerja.
 */
function Kredensial({ id, t, locale }) {
  const q = useKredensial(id)
  const muat = useMuatKredensial(id)
  const [ganti, setGanti] = useState(null)   // provider yang sedang diganti
  const [token, setToken] = useState('')
  const [sampai, setSampai] = useState('')
  const NADA = { active: 'ok', rotated: 'idle', revoked: 'bad', expired: 'warn' }

  // Penyedia yang punya baris AKTIF — cuma itu yang bisa diganti, dan cuma sekali
  // per penyedia (baris 'rotated' adalah jejak, bukan sesuatu yang bisa disunting).
  const aktif = (q.data ?? []).filter((k) => k.status === 'active')

  function kirim(provider) {
    muat.mutate(
      {
        provider,
        nilai: token.trim(),
        expires_at: sampai ? new Date(sampai).toISOString() : null,
      },
      { onSuccess: () => { setGanti(null); setToken(''); setSampai('') } },
    )
  }

  return (
    <Panel judul={t('sb.kredJudul')} catatan={t('sb.kredCatatan')}>
      {q.isLoading && <Memuat tinggi={3} />}
      {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}
      {q.data && (q.data.length === 0 ? (
        <Kosong judul={t('sb.kredKosong')} keterangan={t('sb.kredKosongKet')} />
      ) : (
        <Tabel kepala={[t('sb.kolPenyedia'), t('sb.kolDiisi'), t('sb.kolKedaluwarsa'),
                        t('sb.kolBisa'), '']}>
          {q.data.map((k, i) => (
            <Baris key={`${k.provider}-${k.credential_type}-${i}`}>
              <td className="font-mono text-[11.5px]">{k.provider}</td>
              <td className="font-mono text-[11.5px] text-ink2">{tanggal(k.diisi, locale)}</td>
              <td className="font-mono text-[11.5px] text-ink2">
                {k.kedaluwarsa ? tanggal(k.kedaluwarsa, locale) : <span className="text-ink3">—</span>}
              </td>
              <td><Chip nada={NADA[k.status] ?? 'idle'}>{k.status}</Chip></td>
              <td className="whitespace-nowrap text-right">
                {k.status === 'active' && (
                  <Tombol
                    kecil
                    jenis="sekunder"
                    onClick={() => {
                      setGanti(ganti === k.provider ? null : k.provider)
                      setToken(''); setSampai(''); muat.reset()
                    }}
                  >
                    {ganti === k.provider ? t('umum.batal') : t('sb.gantiToken')}
                  </Tombol>
                )}
              </td>
            </Baris>
          ))}
        </Tabel>
      ))}
      {ganti && (
        <div className="p-3.5 border-t border-line flex flex-col gap-3 max-w-[70ch]">
          <Bendera nada="warn" judul={t('sb.gantiJudul', { provider: ganti })}>
            <p>{t('sb.gantiPeringatan')}</p>
          </Bendera>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
              {t('sb.tokenBaru')}
            </span>
            {/* type="text", BUKAN "password". Token yang tertutup titik-titik tidak bisa
                diperiksa mata sebelum dikirim — dan kegagalan paling sering di layar ini
                adalah salinan yang terpotong, yang justru kelihatan kalau ekornya terbaca. */}
            <Isian
              value={token}
              spellCheck={false}
              autoComplete="off"
              placeholder="EAA…"
              onChange={(e) => setToken(e.target.value)}
            />
            <span className="text-[11px] text-ink3">
              {t('sb.panjangToken', { n: token.trim().length })}
            </span>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
              {t('sb.kedaluwarsaOpsional')}
            </span>
            <Isian
              type="date"
              className="max-w-[200px]"
              value={sampai}
              onChange={(e) => setSampai(e.target.value)}
            />
            <span className="text-[11px] text-ink3">{t('sb.kedaluwarsaCatatan')}</span>
          </label>

          {muat.isError && <Bendera nada="bad" role="alert">{muat.error?.message}</Bendera>}

          <div>
            <Tombol disabled={!token.trim() || muat.isPending} onClick={() => kirim(ganti)}>
              {muat.isPending ? t('umum.menyimpan') : t('sb.simpanToken')}
            </Tombol>
          </div>
        </div>
      )}

      <p className="m-0 px-3.5 py-2.5 text-[11.5px] text-ink2 border-t border-line max-w-[80ch]">
        {t('sb.kredKaki')}
      </p>
    </Panel>
  )
}

/**
 * TUKAR TOKEN META — token Pengguna berumur satu jam jadi token Halaman yang tidak mati.
 *
 * Ada karena jalur manualnya sudah gagal sekali: 10 September sambungan Facebook tenant
 * ini mati satu jam sesudah dipasang, dan gejalanya baru terlihat saat ada yang mencoba
 * menerbitkan. Token dari Graph API Explorer memang berumur pendek, dan token Halaman
 * yang diturunkan darinya ikut mati bersamanya.
 *
 * Yang ditempel di sini token PENGGUNA, bukan token Halaman — dan itu justru yang
 * membuatnya lebih mudah: tidak perlu memilih Halaman di dropdown Explorer yang selalu
 * kembali ke token Pengguna tiap kali digenerate.
 */
function TukarToken({ id, t }) {
  const tukar = useTukarTokenMeta(id)
  const [token, setToken] = useState('')
  const [pageId, setPageId] = useState('')

  return (
    <Panel judul={t('sb.tukarJudul')} catatan={t('sb.tukarCatatan')}>
      <Body className="flex flex-col gap-3 max-w-[70ch]">
        <Bendera nada="info">{t('sb.tukarIsi')}</Bendera>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
            {t('sb.tokenPengguna')}
          </span>
          <Isian
            value={token}
            spellCheck={false}
            autoComplete="off"
            placeholder="EAA…"
            onChange={(e) => setToken(e.target.value)}
          />
          <span className="text-[11px] text-ink3">
            {t('sb.panjangToken', { n: token.trim().length })}
          </span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
            {t('sb.pageIdOpsional')}
          </span>
          <Isian
            className="max-w-[240px] font-mono"
            value={pageId}
            placeholder="1355071434345607"
            onChange={(e) => setPageId(e.target.value)}
          />
          <span className="text-[11px] text-ink3">{t('sb.pageIdCatatan')}</span>
        </label>

        {tukar.isError && <Bendera nada="bad" role="alert">{tukar.error?.message}</Bendera>}
        {tukar.data && (
          <Bendera nada="ok" role="status" judul={t('sb.tukarBerhasil', { nama: tukar.data.nama_halaman })}>
            <p>
              {tukar.data.kedaluwarsa
                ? t('sb.tukarMasihBerbatas', { tanggal: tukar.data.kedaluwarsa })
                : t('sb.tukarAbadi')}
            </p>
            {/* Sebabnya, bukan cuma gejalanya. Kalau token PENGGUNA hasil penukaran juga
                berbatas, yang gagal langkah penukarannya — bukan pengambilan token
                Halaman. Tanpa baris ini orang mengulang langkah yang salah. */}
            {tukar.data.kedaluwarsa && tukar.data.pengguna_kedaluwarsa && (
              <p>{t('sb.tukarSebabnya', { tanggal: tukar.data.pengguna_kedaluwarsa })}</p>
            )}
          </Bendera>
        )}

        <div>
          <Tombol
            disabled={!token.trim() || tukar.isPending}
            onClick={() => tukar.mutate(
              { user_token: token.trim(), page_id: pageId.trim() || null },
              { onSuccess: () => { setToken(''); setPageId('') } },
            )}
          >
            {tukar.isPending ? t('sb.menukar') : t('sb.tukarSekarang')}
          </Tombol>
        </div>
      </Body>
    </Panel>
  )
}

export default function Sambungan({ id, t, locale }) {
  return (
    <>
      <Bendera nada="info" judul={t('sb.spanduk')}>
        <p>{t('sb.spandukIsi')}</p>
      </Bendera>

      <TambahAkun id={id} t={t} />
      <PetaPlatform id={id} t={t} />
      <TukarToken id={id} t={t} />

      <Kolom2>
        <Kredensial id={id} t={t} locale={locale} />
        <GrupTelegram id={id} t={t} />
      </Kolom2>
    </>
  )
}
